import React from 'react';
import { Play, Sparkles, CheckCircle2, Award, Zap, Shield, HelpCircle, Activity, Gauge, Flame, Smartphone, Mail, LayoutDashboard, BookOpen, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { TrackCard } from '../components/common/TrackCard';
import { TrackSummary, TrackType, User, Subscription, isProSubscriber } from '../../shared/types';

function AppleStoreIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 6.37c.61-.75 1.04-1.8 1.01-2.87-.96.04-2.13.65-2.79 1.42-.58.68-.99 1.76-.92 2.81 1.08.08 2.12-.56 2.7-1.36z" />
    </svg>
  );
}

function GooglePlayStoreIcon({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M3.609 1.814L13.792 12 3.61 22.186c-.368-.387-.61-.94-.61-1.619V3.433c0-.68.242-1.232.61-1.619z" fill="#00E676" />
      <path d="M17.155 8.637l-3.363 3.363 3.363 3.363 3.79-2.166c.866-.495.866-1.9 0-2.394l-3.79-2.166z" fill="#FFD600" />
      <path d="M13.792 12L3.61 1.814c.473-.497 1.247-.565 1.95-.164l11.595 6.626-3.363 3.724z" fill="#00B0FF" />
      <path d="M13.792 12l3.363 3.724-11.595 6.626c-.703.401-1.477.333-1.95-.164L13.792 12z" fill="#FF3D00" />
    </svg>
  );
}

export interface HomePageProps {
  tracks: TrackSummary[];
  user?: User | null;
  subscription?: Subscription | null;
  onSelectTrack: (track: TrackType) => void;
  onStartStudy: (track: TrackType | 'all') => void;
  onStartMock: (track: TrackType | 'all') => void;
  onOpenAuthModal: () => void;
  onNavigate?: (path: string) => void;
}

export function HomePage({
  tracks,
  user,
  subscription,
  onSelectTrack,
  onStartStudy,
  onStartMock,
  onOpenAuthModal,
  onNavigate,
}: HomePageProps) {
  const isPro = isProSubscriber(subscription);

  return (
    <div className="space-y-16 pb-16">
      {/* Hero Section */}
      <section className="relative overflow-hidden pt-12 pb-16 md:pt-20 md:pb-24 border-b border-border/60 bg-radial-glow">
        <div className="container relative z-10 max-w-5xl text-center">
          {user ? (
            <div className="inline-flex items-center gap-2.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-4 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-300 mb-6 backdrop-blur-sm shadow-sm">
              <span className="flex h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
              <span>Candidate Portal: <strong>{user.name}</strong></span>
              <span className="text-emerald-500">•</span>
              <span className="uppercase text-[10px] tracking-wider font-bold">
                {isPro ? 'Pro Active' : 'Free Tier'}
              </span>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-3.5 py-1 text-xs font-semibold text-amber-600 dark:text-amber-400 mb-6 backdrop-blur-sm">
              <Flame className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400" />
              Independent Prep for Ramsay &amp; USPS Exam 955 Standards
            </div>
          )}

          <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight text-foreground dark:text-white mb-6 leading-tight">
            {user ? (
              <>
                Welcome Back, <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-600 dark:from-amber-400 dark:via-amber-300 dark:to-yellow-500">{user.name.split(' ')[0]}</span>. Continue Your Assessment Prep.
              </>
            ) : (
              <>
                Pass the <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-600 via-amber-500 to-yellow-600 dark:from-amber-400 dark:via-amber-300 dark:to-yellow-500">Ramsay &amp; USPS Maintenance Exams</span> on Your First Attempt.
              </>
            )}
          </h1>

          <p className="mx-auto max-w-2xl text-base md:text-lg text-slate-600 dark:text-slate-300 mb-8 leading-relaxed">
            {user ? (
              <>
                Pick up where you left off with full-length timed mock exams, interactive schematics, and performance analytics for Ramsay and USPS Exam 955 certification tracks.
              </>
            ) : (
              <>
                High-yield practice exams, realistic circuit &amp; mechanical schematics, and rigorous 60-minute timed simulators built specifically for industrial maintenance technicians and USPS Exam 955 candidates (Maintenance Mechanic, MPE, BEM, and ET).
              </>
            )}
          </p>

          {/* Call to action buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {user ? (
              <>
                <Button
                  variant="amber"
                  size="lg"
                  onClick={() => onNavigate ? onNavigate('/dashboard') : undefined}
                  className="w-full sm:w-auto text-base gap-2 px-8 shadow-lg shadow-amber-500/20 font-bold"
                >
                  <LayoutDashboard className="h-4 w-4" />
                  Open Candidate Dashboard
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => onStartMock('all')}
                  className="w-full sm:w-auto text-base gap-2 px-8 shadow-sm"
                >
                  <Play className="h-4 w-4 fill-current text-amber-500" />
                  Launch 60-Min Mock Exam
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="amber"
                  size="lg"
                  onClick={() => onStartMock('all')}
                  className="w-full sm:w-auto text-base gap-2 px-8 shadow-lg shadow-amber-500/20"
                >
                  <Play className="h-4 w-4 fill-current" />
                  Start Full 60-Min Mock Exam
                </Button>
                <Button
                  variant="secondary"
                  size="lg"
                  onClick={() => onStartStudy('all')}
                  className="w-full sm:w-auto text-base gap-2 px-8 shadow-sm"
                >
                  <Sparkles className="h-4 w-4 text-amber-500 dark:text-amber-400" />
                  Practice Untimed Study Mode
                </Button>
              </>
            )}
          </div>

          {/* Target Exam Trust Badges */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2 max-w-3xl mx-auto">
            <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mr-1">Covers:</span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-amber-500/10 text-amber-700 dark:text-amber-300 border border-amber-500/20">
              Ramsay MEC-TEST
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-sky-500/10 text-sky-700 dark:text-sky-300 border border-sky-500/20">
              Ramsay ELE-TEST
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-500/20">
              Ramsay MultiCrafTest
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-xs font-bold bg-blue-600/15 text-blue-700 dark:text-blue-300 border border-blue-500/30">
              <Mail className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
              USPS Postal Exam 955 (MM / MPE / BEM / ET)
            </span>
            <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20">
              Ramsay MAT Aptitude
            </span>
          </div>

          {/* Key Metrics / Highlights */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-12 pt-10 border-t border-border dark:border-slate-800/80 text-center">
            <div className="p-3">
              <div className="text-2xl md:text-3xl font-bold text-foreground dark:text-white font-mono">4 Tracks</div>
              <div className="text-xs text-muted-foreground mt-1">Mechanical, Electrical, Multicraft, Aptitude</div>
            </div>
            <div className="p-3">
              <div className="text-2xl md:text-3xl font-bold text-amber-600 dark:text-amber-400 font-mono">70% Pass</div>
              <div className="text-xs text-muted-foreground mt-1">Standard Cutoff Benchmark</div>
            </div>
            <div className="p-3">
              <div className="text-2xl md:text-3xl font-bold text-sky-600 dark:text-sky-400 font-mono">Real Schematics</div>
              <div className="text-xs text-muted-foreground mt-1">PLCs, Hydraulics, P&ID, Wiring</div>
            </div>
            <div className="p-3">
              <div className="text-2xl md:text-3xl font-bold text-emerald-600 dark:text-emerald-400 font-mono">Timed Mocks</div>
              <div className="text-xs text-muted-foreground mt-1">60-Min Realistic Simulations</div>
            </div>
          </div>
        </div>
      </section>

      {/* Tracks Selection Grid */}
      <section className="container max-w-6xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2 text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-1">
              <Gauge className="h-4 w-4" />
              Specialized Modules
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground dark:text-white tracking-tight">
              Select Your Assessment Track
            </h2>
          </div>
          <p className="text-sm text-muted-foreground max-w-md">
            All tracks directly map to Ramsay Maintenance benchmark categories and USPS Postal Exam 955 test domains.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {tracks.map((track) => (
            <TrackCard
              key={track.track}
              track={track}
              onSelectTrack={onSelectTrack}
              onStartStudy={onStartStudy}
              onStartMock={onStartMock}
            />
          ))}
        </div>
      </section>

      {/* Featured Electrical Schematics Course Banner */}
      <section className="container max-w-6xl">
        <div className="relative overflow-hidden rounded-2xl border-2 border-sky-500/40 bg-gradient-to-r from-sky-950/80 via-slate-900 to-amber-950/30 p-6 md:p-8 shadow-lg">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
            <div className="space-y-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-sky-500/30 bg-sky-500/15 px-3 py-1 text-xs font-bold text-sky-400">
                <BookOpen className="h-3.5 w-3.5" />
                Featured Technical Curriculum • Comprehensive Schematics Masterclass
              </div>
              <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
                Complete Electrical Schematics &amp; Blueprint Reading Course
              </h2>
              <p className="text-sm text-slate-300 max-w-2xl leading-relaxed">
                Step-by-step masterclass covering schematic symbology, block diagrams, signal tracing across multi-page blueprints, passive and active semiconductors, op-amps, and component-level troubleshooting.
              </p>
              <div className="flex flex-wrap items-center gap-4 text-xs font-mono text-muted-foreground pt-1">
                <span className="flex items-center gap-1.5 text-sky-400 font-bold">
                  <CheckCircle2 className="h-4 w-4" /> 24 Illustrated Lessons
                </span>
                <span className="flex items-center gap-1.5 text-amber-400 font-bold">
                  <CheckCircle2 className="h-4 w-4" /> 5 Interactive Diagnostic Tools
                </span>
                <span className="flex items-center gap-1.5 text-emerald-400 font-bold">
                  <CheckCircle2 className="h-4 w-4" /> 30-Question Certification Exam
                </span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0 w-full sm:w-auto">
              <Button
                variant="amber"
                size="lg"
                onClick={() => onNavigate?.('/course/schematics')}
                className="font-bold gap-2 shadow-lg shadow-amber-500/20 whitespace-nowrap"
              >
                <span>Launch Schematics Course</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* USPS Postal Exam 955 Section */}
      <section className="container max-w-6xl">
        <div className="relative overflow-hidden rounded-2xl border border-blue-500/30 bg-gradient-to-br from-blue-500/10 via-card to-amber-500/5 p-8 md:p-12 shadow-sm">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            <div className="md:col-span-8 space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/15 px-3 py-1 text-xs font-bold text-blue-600 dark:text-blue-400">
                <Mail className="h-3.5 w-3.5" />
                United States Postal Service • Maintenance Craft Assessment
              </div>

              <h2 className="text-2xl md:text-3xl font-extrabold text-foreground dark:text-white tracking-tight leading-tight">
                Complete Preparation for the USPS Postal Exam 955
              </h2>

              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                USPS Postal Exam 955 is the primary qualification test for high-paying maintenance craft positions across USPS Processing & Distribution Centers (P&DCs) and Network Distribution Centers (NDCs). MaintenancePrep provides the exact technical practice, motor control schematics, and spatial visualization tests needed to achieve top-tier placement:
              </p>

              {/* Position Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
                <div className="rounded-xl border border-border dark:border-slate-800 bg-card/90 dark:bg-slate-900/90 p-3 text-center shadow-xs">
                  <div className="text-xs font-bold text-blue-600 dark:text-blue-400">MM-7</div>
                  <div className="text-[11px] font-semibold text-foreground dark:text-white mt-0.5">Maintenance Mechanic</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">Pneumatics & Tools</div>
                </div>
                <div className="rounded-xl border border-border dark:border-slate-800 bg-card/90 dark:bg-slate-900/90 p-3 text-center shadow-xs">
                  <div className="text-xs font-bold text-sky-600 dark:text-sky-400">MPE-9</div>
                  <div className="text-[11px] font-semibold text-foreground dark:text-white mt-0.5">Mail Processing Equip.</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">Conveyors & Sorting</div>
                </div>
                <div className="rounded-xl border border-border dark:border-slate-800 bg-card/90 dark:bg-slate-900/90 p-3 text-center shadow-xs">
                  <div className="text-xs font-bold text-amber-600 dark:text-amber-400">BEM-9</div>
                  <div className="text-[11px] font-semibold text-foreground dark:text-white mt-0.5">Building Equip. Mechanic</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">HVAC, Pumps & Power</div>
                </div>
                <div className="rounded-xl border border-border dark:border-slate-800 bg-card/90 dark:bg-slate-900/90 p-3 text-center shadow-xs">
                  <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400">ET-10</div>
                  <div className="text-[11px] font-semibold text-foreground dark:text-white mt-0.5">Electronic Technician</div>
                  <div className="text-[10px] text-muted-foreground mt-0.5">PLCs, Logic & Circuits</div>
                </div>
              </div>

              {/* Exam Parts Checklist */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2 text-xs font-medium text-foreground dark:text-slate-200">
                  <CheckCircle2 className="h-4 w-4 text-blue-500 shrink-0" />
                  <span>Part 1: Multicraft Technical, Motors, Hydraulics & Schematics</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-medium text-foreground dark:text-slate-200">
                  <CheckCircle2 className="h-4 w-4 text-blue-500 shrink-0" />
                  <span>Part 2: Spatial Relations & 3D Mechanical Reasoning</span>
                </div>
              </div>
            </div>

            {/* Callout box */}
            <div className="md:col-span-4 flex flex-col justify-center">
              <div className="rounded-xl border border-blue-500/20 bg-card dark:bg-slate-900/95 p-6 space-y-4 shadow-md text-center sm:text-left">
                <div className="space-y-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-blue-600 dark:text-blue-400">Postal Craft Target</span>
                  <h3 className="text-lg font-bold text-foreground dark:text-white">Exam 955 Cutoff Strategy</h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    USPS promotion and hiring registers rank candidates by score (70 passing, 80+ for competitive placement). Master timing and technical schematics before test day.
                  </p>
                </div>
                <Button
                  variant="amber"
                  size="md"
                  onClick={() => onStartMock('multicraft')}
                  className="w-full text-xs font-bold shadow-md shadow-amber-500/15"
                >
                  Start Multicraft / USPS 955 Mock
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Apps Coming Soon Section */}
      <section className="container max-w-6xl">
        <div className="relative overflow-hidden rounded-2xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-card to-background p-8 md:p-12 shadow-sm">
          <div className="grid md:grid-cols-12 gap-8 items-center">
            {/* Text & Badges */}
            <div className="md:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/15 px-3 py-1 text-xs font-bold text-amber-600 dark:text-amber-400">
                <Smartphone className="h-3.5 w-3.5" />
                iOS & Android Mobile Apps Coming Soon
              </div>

              <h2 className="text-2xl md:text-4xl font-extrabold text-foreground dark:text-white tracking-tight leading-tight">
                Practice On the Go with Native Mobile Apps
              </h2>

              <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                Take full exams and review schematics on your phone or tablet. Our dedicated native apps for iOS (iPhone/iPad) and Android are launching soon with complete offline test taking, pinch-to-zoom diagrams, and live cloud sync with your web account.
              </p>

              {/* Feature Highlights Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2 text-xs font-medium text-foreground dark:text-slate-200">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                  <span>100% Offline Exam Mode</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-medium text-foreground dark:text-slate-200">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                  <span>Pinch-to-Zoom Schematics</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-medium text-foreground dark:text-slate-200">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                  <span>Cross-Device Cloud Sync</span>
                </div>
                <div className="flex items-center gap-2 text-xs font-medium text-foreground dark:text-slate-200">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                  <span>60-Min Realistic Timer</span>
                </div>
              </div>

              {/* Store Download Badges */}
              <div className="flex flex-wrap items-center gap-3 pt-4">
                {/* Apple App Store */}
                <div className="flex items-center gap-3 rounded-xl border border-border dark:border-slate-800 bg-card dark:bg-slate-900/90 px-4 py-2.5 shadow-sm">
                  <AppleStoreIcon className="h-7 w-7 text-foreground dark:text-white fill-current shrink-0" />
                  <div>
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider flex items-center gap-1.5">
                      <span>Coming Soon on</span>
                      <span className="text-[9px] bg-amber-500/20 text-amber-600 dark:text-amber-400 font-bold px-1.5 py-0.2 rounded">Q4 2026</span>
                    </div>
                    <div className="text-sm font-bold text-foreground dark:text-white leading-tight">Apple App Store</div>
                  </div>
                </div>

                {/* Google Play */}
                <div className="flex items-center gap-3 rounded-xl border border-border dark:border-slate-800 bg-card dark:bg-slate-900/90 px-4 py-2.5 shadow-sm">
                  <GooglePlayStoreIcon className="h-7 w-7 shrink-0" />
                  <div>
                    <div className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider flex items-center gap-1.5">
                      <span>Coming Soon on</span>
                      <span className="text-[9px] bg-amber-500/20 text-amber-600 dark:text-amber-400 font-bold px-1.5 py-0.2 rounded">Q4 2026</span>
                    </div>
                    <div className="text-sm font-bold text-foreground dark:text-white leading-tight">Google Play</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile Device Mockup Visual */}
            <div className="md:col-span-5 flex justify-center">
              <div className="relative w-64 rounded-3xl border-4 border-slate-700 dark:border-slate-600 bg-slate-950 p-3 shadow-2xl shadow-amber-500/10">
                {/* Speaker bar & notch */}
                <div className="mx-auto h-3 w-20 rounded-full bg-slate-800 mb-3" />
                
                {/* Simulated Screen */}
                <div className="rounded-2xl bg-slate-900 p-3.5 space-y-3 border border-slate-800 text-left">
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-bold text-white flex items-center gap-1.5">
                      <Gauge className="h-3.5 w-3.5 text-amber-400" />
                      MaintenancePrep
                    </span>
                    <span className="text-[9px] bg-emerald-500/20 text-emerald-400 font-semibold px-1.5 py-0.5 rounded">
                      Offline Ready
                    </span>
                  </div>

                  <div className="rounded-lg bg-slate-800/80 p-2.5 border border-slate-700/60">
                    <div className="text-[10px] text-amber-400 font-semibold uppercase">MEC-TEST / 955 #14</div>
                    <div className="text-[11px] font-medium text-slate-100 mt-1 leading-snug">
                      Which component absorbs hydraulic shock and pressure spikes?
                    </div>
                    <div className="mt-2 space-y-1">
                      <div className="rounded bg-slate-700/60 px-2 py-1 text-[10px] text-slate-300">A. Check Valve</div>
                      <div className="rounded bg-amber-500/20 border border-amber-500/50 px-2 py-1 text-[10px] text-amber-300 font-semibold">B. Bladder Accumulator ✓</div>
                      <div className="rounded bg-slate-700/60 px-2 py-1 text-[10px] text-slate-300">C. Flow Meter</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-[10px] text-slate-400 pt-1">
                    <span>⏱ 48:12 remaining</span>
                    <span className="text-amber-400 font-medium">Question 14/36</span>
                  </div>
                </div>

                {/* Home Indicator */}
                <div className="mx-auto mt-3 h-1 w-24 rounded-full bg-slate-700" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Comparison / Breakdown */}
      <section className="container max-w-6xl">
        <div className="rounded-2xl border border-border dark:border-slate-800 bg-card/60 dark:bg-slate-900/40 p-8 md:p-12 shadow-sm">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl font-bold text-foreground dark:text-white mb-3">
              Why MaintenancePrep Outperforms Generic Study Guides
            </h2>
            <p className="text-sm text-muted-foreground">
              Ramsay tests and USPS Postal Exam 955 don't test textbook memorization—they test practical, fast troubleshooting logic under strict time limits.
            </p>
          </div>

          <div className="grid gap-8 md:grid-cols-3">
            <div className="space-y-3 rounded-xl border border-border dark:border-slate-800 bg-card dark:bg-slate-950/60 p-6 shadow-sm">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-amber-500/10 text-amber-500 dark:text-amber-400">
                <Zap className="h-5 w-5" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-foreground dark:text-white text-base">True 60-Minute Timing</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Experience the exact time pressure of 36–60 questions in one hour. Build pacing strategy so you never run out of time on complex calculation questions.
              </p>
            </div>

            <div className="space-y-3 rounded-xl border border-border dark:border-slate-800 bg-card dark:bg-slate-950/60 p-6 shadow-sm">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-500/10 text-sky-500 dark:text-sky-400">
                <Activity className="h-5 w-5" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-foreground dark:text-white text-base">Industrial Schematics</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Practice diagnosing problems directly on ladder logic rungs, hydraulic valve spools, 3-phase motor starter schematics, and gear train ratios.
              </p>
            </div>

            <div className="space-y-3 rounded-xl border border-border dark:border-slate-800 bg-card dark:bg-slate-950/60 p-6 shadow-sm">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-500 dark:text-emerald-400">
                <Shield className="h-5 w-5" aria-hidden="true" />
              </div>
              <h3 className="font-bold text-foreground dark:text-white text-base">Step-by-Step Logic Proofs</h3>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Every question includes detailed engineering formulas, safety guidelines (OSHA / NFPA 70E), and troubleshooting checklists for instant mastery.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Frequently Asked Questions Section (SEO & Rich Results) */}
      <section className="container max-w-4xl" id="faq" aria-labelledby="faq-heading">
        <div className="text-center mb-10 space-y-3">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-border dark:border-slate-800 bg-card dark:bg-slate-900/60 px-3 py-1 text-xs font-semibold text-muted-foreground">
            <HelpCircle className="h-3.5 w-3.5 text-amber-500" />
            Frequently Asked Questions
          </div>
          <h2 id="faq-heading" className="text-2xl md:text-3xl font-extrabold text-foreground dark:text-white tracking-tight">
            Everything You Need to Know About Ramsay &amp; USPS 955 Exams
          </h2>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Clear answers on test formats, passing benchmarks, postal craft positions, and realistic mock exam simulations.
          </p>
        </div>

        <div className="space-y-4">
          {[
            {
              q: "What is the Ramsay Maintenance Assessment and who takes it?",
              a: "The Ramsay Maintenance Assessment is an industry-standard pre-hire and promotional screening exam published by Ramsay Corporation. Leading manufacturing, automotive, aerospace, and logistics companies (including Amazon, Boeing, Procter & Gamble, General Motors, and US Steel) administer Ramsay exams—such as MEC-TEST (Mechanical), ELE-TEST (Electrical), and MultiCrafTest—to evaluate candidate technical knowledge and troubleshooting ability under timed conditions."
            },
            {
              q: "Does MaintenancePrep cover the USPS Postal Exam 955?",
              a: "Yes! The USPS Postal Exam 955 is the United States Postal Service's primary examination for skilled maintenance craft careers: Maintenance Mechanic (MM-7), Mail Processing Equipment Mechanic (MPE-9), Building Equipment Mechanic (BEM-9), and Electronic Technician (ET-10). Because Exam 955 tests the identical domains of industrial multicraft maintenance (motors, fluid power, ladder logic, schematics, and spatial relations), MaintenancePrep's modules provide 100% domain alignment."
            },
            {
              q: "What is a passing score on Ramsay and USPS 955 exams?",
              a: "Most manufacturing employers establish a 70% cutoff benchmark to pass Ramsay assessments. On the USPS Exam 955, scoring at least 70 qualifies you for placement on the register of eligible applicants. However, hiring and internal promotion priority are ranked strictly by raw exam score—candidates scoring 80% to 90%+ receive the highest placement priority."
            },
            {
              q: "Are the mock exams timed like the actual test?",
              a: "Yes. Our full-length mock exams feature an active 60-minute countdown simulator that accurately mirrors proctored assessment sessions. You can also practice in Untimed Study Mode with immediate answers, circuit diagrams, and step-by-step engineering formulas."
            },
            {
              q: "How does the 14-day free trial work?",
              a: "You get full, unrestricted access to all 4 assessment tracks, unlimited mock exam sessions, and diagnostic analytics for 14 days. If you find it helpful, it's just $9.99/month afterward. You can cancel anytime with one click in your account portal with no obligations."
            }
          ].map((faq, idx) => (
            <details
              key={idx}
              name="homepage-faq"
              className="group rounded-xl border border-border dark:border-slate-800 bg-card/80 dark:bg-slate-900/60 p-5 shadow-xs transition-colors hover:border-amber-500/40"
            >
              <summary className="flex cursor-pointer items-center justify-between font-semibold text-sm md:text-base text-foreground dark:text-white list-none select-none">
                <span>{faq.q}</span>
                <span className="ml-4 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-muted-foreground group-open:rotate-180 transition-transform">
                  ▾
                </span>
              </summary>
              <p className="mt-3 text-xs md:text-sm text-muted-foreground leading-relaxed border-t border-border/60 dark:border-slate-800/60 pt-3">
                {faq.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Dynamic Callout Banner */}
      <section className="container max-w-6xl">
        <div className="relative overflow-hidden rounded-2xl border-2 border-amber-500/30 bg-gradient-to-r from-amber-500/10 via-card to-amber-500/5 p-8 md:p-12 shadow-lg shadow-amber-500/5 text-center sm:text-left flex flex-col sm:flex-row items-center justify-between gap-6">
          <div className="space-y-2 max-w-xl">
            <div className="inline-flex items-center gap-1.5 text-xs font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
              <Zap className="h-4 w-4 fill-current" />
              {user ? (isPro ? 'Pro Member Access' : 'Upgrade to Pro') : '14-Day Free Trial • Risk Free'}
            </div>
            <h2 className="text-2xl md:text-3xl font-extrabold text-foreground dark:text-white tracking-tight">
              {user
                ? isPro
                  ? 'Ready to Continue Your Preparation?'
                  : 'Unlock Full Unlimited Mock Simulations'
                : 'Ready to Pass Your Ramsay or USPS Maintenance Exam?'}
            </h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              {user
                ? isPro
                  ? 'Access all 4 tracks, review previous test session scores, and continue mastering high-yield schematics in your candidate dashboard.'
                  : 'Upgrade your account to unlock unlimited full-length 60-minute mock simulations, complete circuit schematics, and detailed performance metrics. Free for 14 days, then $9.99/mo.'
                : 'Start your 14-day free trial today with instant access to all full-length mock exams, diagnostic tracking, and realistic troubleshooting schematics covering Ramsay standards and USPS Exam 955 domains. Then just $9.99/month. Cancel anytime.'}
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0">
            {user ? (
              isPro ? (
                <Button
                  variant="amber"
                  size="lg"
                  onClick={() => onNavigate ? onNavigate('/dashboard') : undefined}
                  className="text-base font-bold shadow-lg shadow-amber-500/20 px-8 py-3"
                >
                  Go to Dashboard
                </Button>
              ) : (
                <Button
                  variant="amber"
                  size="lg"
                  onClick={() => onNavigate ? onNavigate('/pricing') : undefined}
                  className="text-base font-bold shadow-lg shadow-amber-500/20 px-8 py-3"
                >
                  Start 14-Day Free Trial
                </Button>
              )
            ) : (
              <Button
                variant="amber"
                size="lg"
                onClick={() => onNavigate ? onNavigate('/pricing') : onOpenAuthModal()}
                className="text-base font-bold shadow-lg shadow-amber-500/20 px-8 py-3"
              >
                Start 14-Day Free Trial
              </Button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
